import React, { createContext, useMemo } from 'react';

import { useEnvironment } from '@wix/yoshi-flow-editor';

import { createSiteEcomService, SiteEcomService } from '@/services/siteEcom';
import {
  createVisitorPresenceService,
  VisitorPresenceService,
} from '@/services/visitorPresence';
import {
  createDuplexerConnection,
  DuplexerConnection,
} from '@/services/duplexer';

import { SiteEcomApi } from 'AiAssistantWidget/types';

import { useHttpClient } from './HttpClientProvider';

interface WidgetServices {
  siteEcomService: SiteEcomService;
  duplexerConnection: null | DuplexerConnection;
  visitorPresenceService: VisitorPresenceService;
}
const ServicesContext = createContext<WidgetServices>({
  get siteEcomService(): SiteEcomService {
    throw new Error('siteEcomService is not provided');
  },
  get duplexerConnection(): DuplexerConnection {
    throw new Error('duplexerConnection is not provided');
  },
  get visitorPresenceService(): VisitorPresenceService {
    throw new Error('visitorPresenceService is not provided');
  },
});

export const WidgetServicesProvider: React.FC<{
  instance?: string;
  duplexerConnection?: DuplexerConnection;
  ecom: SiteEcomApi;
}> = ({ children, instance, ecom, ...provided }) => {
  const httpClient = useHttpClient();
  const { isEditor } = useEnvironment();

  const siteEcomService = createSiteEcomService(ecom);

  const duplexerConnection = useMemo(() => {
    if (provided.duplexerConnection) {
      return provided.duplexerConnection;
    }

    if (isEditor || !instance) {
      return null;
    }

    return createDuplexerConnection(instance);
  }, [instance, isEditor, provided.duplexerConnection]);

  const visitorPresenceService = useMemo(
    () => createVisitorPresenceService(httpClient),
    [httpClient],
  );
  return (
    <ServicesContext.Provider
      value={{
        siteEcomService,
        duplexerConnection,
        visitorPresenceService,
      }}
    >
      {children}
    </ServicesContext.Provider>
  );
};

export const useWidgetServices = (): WidgetServices =>
  React.useContext(ServicesContext);
